import { FC, useEffect, useState } from 'react'
import { styled } from 'styled-components'

import { LoadingContainer, Header1, Text, theme } from '@tuhi/components'
import { RouteUrl } from './interface'
import { logEvent } from 'app/logging/firebase'

/**
 * How long to wait on the post-login "Logging in..." screen before assuming the
 * sign-in has stalled. The MSAL redirect + credential exchange normally
 * resolves in a few seconds; if it hasn't after this, surface a recovery action
 * instead of spinning forever (ACA-79).
 */
export const AUTHENTICATING_TIMEOUT_MS = 30_000

const Loading = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
`

const ErrorContainer = styled.div`
    width: 100%;
    min-height: 60vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 0 24px;
    text-align: center;
`

const RetryButton = styled.button`
    padding: 12px 24px;
    border: none;
    border-radius: 8px;
    background: ${theme.colors.primary.default};
    color: ${theme.colors.white};
    font-size: 16px;
    font-weight: bold;
    cursor: pointer;

    &:hover {
        opacity: 0.9;
    }
`

export const AuthenticatingRoute: FC = () => {
    const [timedOut, setTimedOut] = useState(false)

    useEffect(() => {
        const timer = setTimeout(
            () => setTimedOut(true),
            AUTHENTICATING_TIMEOUT_MS
        )
        return () => clearTimeout(timer)
    }, [])

    if (timedOut) {
        logEvent(
            'WARN',
            'authenticating_timeout',
            'Sign-in stalled on the authenticating screen'
        )

        const returnToSignIn = () => {
            // Hard navigation to fully reset any stuck MSAL/auth state that left
            // the redirect exchange hanging.
            window.location.assign(RouteUrl.LogIn.path)
        }

        return (
            <ErrorContainer>
                <Header1>Sign-in is taking longer than expected</Header1>
                <Text size="medium" $weight="regular">
                    Something interrupted signing you in. Please try again.
                </Text>
                <RetryButton onClick={returnToSignIn} type="button">
                    Return to sign in
                </RetryButton>
            </ErrorContainer>
        )
    }

    return (
        <Loading>
            <LoadingContainer text="Logging in..." isLoading={true} />
        </Loading>
    )
}
